import { Controller } from "@hotwired/stimulus"
import { requestSkipWaiting, UPDATE_EVENT, STATUS_EVENT } from "../pwa/register"
import {
  enableOfflineStorage,
  disableOfflineStorage,
  clearAllOfflineData,
  getActiveSnapshot,
  getIdentity,
  isSnapshotExpired,
  isStandalonePwa
} from "../pwa/snapshot"
import { OUTBOX_STATUS_EVENT, publishQueueCount } from "../pwa/outbox"

// 一部だけ保存できたことの通知は常駐させず、一定時間で消す。
const PARTIAL_NOTICE_MS = 8000

// 既存レイアウトを変えず、固定位置の小さな状態表示で PWA / オフライン状態を示す。
export default class extends Controller {
  static targets = ["enableButton", "browserOnlyNotice"]
  static values = {
    state: { type: String, default: "idle" }
  }

  connect() {
    this.ensureStatusEl()
    this.standalone = isStandalonePwa()
    this.applyStandaloneVisibility()
    this.isOffline = !navigator.onLine

    this.onStatus = (event) => {
      if (event?.detail?.state) {
        this.stateValue = event.detail.state
      }
      this.renderStatus(event?.detail || {})
    }
    this.onUpdateReady = (event) => {
      this.stateValue = "update-ready"
      this.pendingRegistration = event.detail?.registration
      this.scheduleSafeReload()
    }
    this.onControllerChange = () => {
      if (this.reloadAfterControllerChange) {
        window.location.reload()
      }
    }
    this.onMessage = (event) => {
      if (event?.data?.type === "pwa-cache-hit") {
        this.isOffline = true
        this.stateValue = "offline"
        this.renderStatus({ state: "offline", fromCache: true })
      }
    }
    this.onOffline = () => {
      this.isOffline = true
      this.stateValue = "offline"
      this.renderStatus({ state: "offline" })
      this.refreshFromStore().catch(() => {})
    }
    this.onOnline = () => {
      this.isOffline = false
      this.stateValue = "online"
      this.renderStatus({ state: "online" })
      this.refreshFromStore().catch(() => {})
    }

    window.addEventListener(STATUS_EVENT, this.onStatus)
    window.addEventListener(OUTBOX_STATUS_EVENT, this.onStatus)
    window.addEventListener(UPDATE_EVENT, this.onUpdateReady)
    window.addEventListener("offline", this.onOffline)
    window.addEventListener("online", this.onOnline)
    if (navigator.serviceWorker && typeof navigator.serviceWorker.addEventListener === "function") {
      navigator.serviceWorker.addEventListener("controllerchange", this.onControllerChange)
      navigator.serviceWorker.addEventListener("message", this.onMessage)
    }

    this.refreshFromStore().catch(() => {})
    publishQueueCount().catch(() => {})
  }

  disconnect() {
    window.removeEventListener(STATUS_EVENT, this.onStatus)
    window.removeEventListener(OUTBOX_STATUS_EVENT, this.onStatus)
    window.removeEventListener(UPDATE_EVENT, this.onUpdateReady)
    window.removeEventListener("offline", this.onOffline)
    window.removeEventListener("online", this.onOnline)
    if (navigator.serviceWorker && typeof navigator.serviceWorker.removeEventListener === "function") {
      navigator.serviceWorker.removeEventListener("controllerchange", this.onControllerChange)
      navigator.serviceWorker.removeEventListener("message", this.onMessage)
    }
    if (this.reloadTimer) {
      window.clearTimeout(this.reloadTimer)
    }
    if (this.hideTimer) {
      window.clearTimeout(this.hideTimer)
    }
  }

  // 自動同期はホーム画面から起動した PWA だけで走る。通常ブラウザで有効化させると
  // 同じ端末の PWA 側と IndexedDB を共有し、通常タブでも同期が走ったと誤解される。
  // 削除・無効化は残す(既に保存した端末で消せなくなるため)。
  applyStandaloneVisibility() {
    this.enableButtonTargets.forEach((el) => setHidden(el, this.standalone === false))
    this.browserOnlyNoticeTargets.forEach((el) => setHidden(el, this.standalone === true))
  }

  async enableOffline(event) {
    event?.preventDefault()
    await enableOfflineStorage()
    await this.refreshFromStore()
  }

  async disableOffline(event) {
    event?.preventDefault()
    await disableOfflineStorage()
    await this.refreshFromStore()
  }

  async clearOffline(event) {
    event?.preventDefault()
    await clearAllOfflineData()
    await this.refreshFromStore()
  }

  async refreshFromStore() {
    if (!navigator.onLine) {
      this.isOffline = true
      this.stateValue = "offline"
      this.renderStatus({ state: "offline" })
    }

    try {
      const identity = await getIdentity()
      const snapshot = await getActiveSnapshot()
      if (!navigator.onLine) {
        this.isOffline = true
        this.stateValue = "offline"
        this.renderStatus({ state: "offline", snapshot })
        return
      }
      if (!identity.enabled) {
        this.stateValue = "offline-disabled"
        this.renderStatus({ state: "offline-disabled" })
        return
      }
      if (snapshot && isSnapshotExpired(snapshot)) {
        this.stateValue = this.standalone ? "expired" : "offline-disabled"
        this.renderStatus({ state: this.stateValue, snapshot })
        return
      }
      if (snapshot) {
        this.stateValue = "synced"
        this.renderStatus({ state: "synced", snapshot })
      }
    } catch (_error) {
      // IndexedDB 非対応環境では状態表示をスキップ
    }
  }

  ensureStatusEl() {
    const existing = document.getElementById("firmee-pwa-status")
    if (existing) {
      this.statusEl = existing
      return
    }
    const el = document.createElement("div")
    el.id = "firmee-pwa-status"
    el.setAttribute("role", "status")
    el.setAttribute("aria-live", "polite")
    el.setAttribute("aria-atomic", "true")
    el.style.cssText = "position:fixed;left:12px;bottom:12px;z-index:1050;max-width:280px;padding:8px 10px;border-radius:4px;background:rgba(0,0,0,.78);box-shadow:0 2px 8px rgba(0,0,0,.2);color:#fff;font-size:12px;font-weight:600;line-height:1.4;display:none;"
    document.body.appendChild(el)
    this.statusEl = el
  }

  renderStatus(detail = {}) {
    const el = this.statusEl || document.getElementById("firmee-pwa-status")
    if (!el) return
    const state = detail.state || this.stateValue
    const snapshot = detail.snapshot
    const offline = this.isOffline || state === "offline" || detail.fromCache
    let text = ""
    let autoHideMs = 0

    if (this.hideTimer) {
      window.clearTimeout(this.hideTimer)
      this.hideTimer = null
    }

    if (offline) {
      const synced = snapshot?.syncedAt ? formatStamp(snapshot.syncedAt) : null
      if (state === "outbox" && detail.count > 0) {
        text = `オフライン — 送信待ち ${detail.count}件`
      } else {
        text = synced ? `オフライン — ${synced} 時点のデータ` : "オフライン"
      }
    } else if (state === "syncing") {
      text = "オフラインデータを更新中"
    } else if (state === "sync-failed") {
      text = "前回のデータを使用しています"
    } else if (state === "auth-expired") {
      text = "再ログイン後に送信します"
    } else if (state === "outbox" && detail.count > 0) {
      text = `送信待ち ${detail.count}件`
    } else if (state === "expired") {
      // 通常ブラウザには準備手段が無いので、対処できない催促は出さない。
      text = this.standalone ? "オンライン接続後にオフラインデータを準備してください" : ""
    } else if (state === "synced" && snapshot) {
      text = partialSnapshotText(snapshot)
      autoHideMs = text ? PARTIAL_NOTICE_MS : 0
    } else if (state === "offline-cleared" || state === "offline-disabled" || state === "outbox-empty") {
      text = ""
    }

    el.dataset.state = offline ? "offline" : state
    el.style.background = offline ? "rgba(180,83,9,.96)" : "rgba(0,0,0,.78)"

    if (!text) {
      el.style.display = "none"
      el.textContent = ""
      return
    }
    el.textContent = text
    el.style.display = "block"

    if (autoHideMs) {
      this.hideTimer = window.setTimeout(() => {
        this.hideTimer = null
        el.style.display = "none"
        el.textContent = ""
      }, autoHideMs)
    }
  }

  scheduleSafeReload() {
    if (this.reloadTimer) {
      window.clearTimeout(this.reloadTimer)
    }

    const attempt = () => {
      if (this.isUnsafeToReload()) {
        this.reloadTimer = window.setTimeout(attempt, 1500)
        return
      }
      this.reloadAfterControllerChange = true
      requestSkipWaiting(this.pendingRegistration)
    }

    this.reloadTimer = window.setTimeout(attempt, 0)
  }

  isUnsafeToReload() {
    const active = document.activeElement
    if (!active) return false
    const tag = active.tagName
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true
    if (active.isContentEditable) return true
    return false
  }
}

// Bootstrap 3 には [hidden] のルールが無く、.btn の display が hidden 属性に勝つ。
// 属性だけでは隠れないので、インラインスタイルも合わせて切り替える。
function setHidden(el, hidden) {
  el.hidden = hidden
  el.style.display = hidden ? "none" : ""
}

// catalog 全件が収まらなかったときだけ件数を返す。全件保存できたら通知しない。
function partialSnapshotText(snapshot) {
  const total = Number(snapshot.urlCount)
  const stored = Number(snapshot.storedCount)
  if (!Number.isFinite(total) || !Number.isFinite(stored) || stored >= total) return ""
  return `オフラインデータ ${total}件中${stored}件を保存しました`
}

function formatStamp(iso) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  const pad = (n) => String(n).padStart(2, "0")
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}
